import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ScopeEmptyState } from "@/components/findings-scope/scope-empty-state";
import {
  isScoped,
  hasBuildScope,
  parseScope,
  scopeParams,
} from "@/components/findings-scope/scope-url-state";
import { FilteredEmptyState } from "@/components/ui/filtered-empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { STALE_TIMES } from "@/lib/query-performance";
import { RouteList } from "./route-list";
import {
  RouteFindingsList,
  type RouteCanonicalItem,
} from "./route-findings-list";
import { FindingDetailDrawer } from "@/components/triage/finding-detail-drawer";
import {
  PagesCoverageBanner,
  PagesZeroUrlsEmptyState,
} from "./pages-empty-states";

interface PagesViewProps {
  projectId: string;
}

export function PagesView({ projectId }: PagesViewProps) {
  const [params, setParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCanonicalId, setSelectedCanonicalId] = useState<string | null>(
    null,
  );

  const routeParam = params.get("route");
  const showAll = params.get("all") === "true";
  // The page-wide scope (period, persona, capability, severity, lens, build).
  const scopeKey = scopeParams(params).toString();
  const scope = useMemo(
    () => parseScope(new URLSearchParams(scopeKey)),
    [scopeKey],
  );

  const routesQuery = trpc.pageFindings.routes.useQuery(
    { projectId, scope },
    { staleTime: STALE_TIMES.list },
  );

  const routes = useMemo(
    () => routesQuery.data?.routes ?? [],
    [routesQuery.data],
  );

  // Determine active route key: prefer URL param, fallback to first route
  const activeRouteKey = useMemo(() => {
    if (routeParam && routes.some((r) => r.routeKey === routeParam)) {
      return routeParam;
    }
    return routes[0]?.routeKey ?? null;
  }, [routeParam, routes]);

  const activeRouteItem = useMemo(
    () => routes.find((r) => r.routeKey === activeRouteKey) ?? null,
    [routes, activeRouteKey],
  );

  const handleSelectRoute = useCallback(
    (newRouteKey: string) => {
      setSelectedCanonicalId(null);
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", "pages");
          next.set("route", newRouteKey);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const handleToggleShowAll = useCallback(
    (show: boolean) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (show) {
            next.set("all", "true");
          } else {
            next.delete("all");
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const findingsQuery = trpc.pageFindings.forRoute.useQuery(
    {
      projectId,
      routeKey: activeRouteKey ?? "",
      includeAll: showAll,
      scope,
    },
    {
      enabled: Boolean(activeRouteKey),
      staleTime: STALE_TIMES.list,
    },
  );

  const canonicals: RouteCanonicalItem[] = useMemo(
    () => findingsQuery.data?.canonicals ?? [],
    [findingsQuery.data],
  );

  // Active canonical for evidence drawer: user clicked row
  const activeCanonical = useMemo(() => {
    if (selectedCanonicalId) {
      return canonicals.find((c) => c.id === selectedCanonicalId) ?? null;
    }
    return null;
  }, [selectedCanonicalId, canonicals]);

  if (routesQuery.isLoading) {
    return (
      <div className="grid gap-6 lg:grid-cols-[13rem_minmax(0,1fr)]">
        <div className="space-y-3">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (!routesQuery.data || routes.length === 0) {
    return isScoped(scope) ? (
      scope.period || hasBuildScope(scope) ? (
        <ScopeEmptyState what="visited pages" />
      ) : (
        <FilteredEmptyState
          title="No pages match this scope"
          description="No walked step matches the persona or capability in the scope above."
        />
      )
    ) : (
      <PagesZeroUrlsEmptyState projectId={projectId} />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <PagesCoverageBanner
        projectId={projectId}
        stepsWithUrl={routesQuery.data.stepsWithUrl}
        totalSteps={routesQuery.data.totalSteps}
        coverageShare={routesQuery.data.coverageShare}
      />

      <div className="grid gap-6 lg:grid-cols-[13rem_minmax(0,1fr)]">
        <aside className="min-w-0">
          <RouteList
            routes={routes}
            selectedRouteKey={activeRouteKey}
            onSelectRoute={handleSelectRoute}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        </aside>

        <div className="min-w-0 space-y-4">
          {activeRouteKey ? (
            findingsQuery.isLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            ) : (
              <RouteFindingsList
                routeKey={activeRouteKey}
                samplePageUrl={activeRouteItem?.samplePageUrl ?? null}
                stepCount={activeRouteItem?.stepCount ?? 0}
                canonicals={canonicals}
                selectedCanonicalId={selectedCanonicalId}
                onSelectCanonical={setSelectedCanonicalId}
                showAllStates={showAll}
                onToggleShowAllStates={handleToggleShowAll}
              />
            )
          ) : (
            <div className="py-12 text-center text-xs text-muted-foreground">
              Select a route to view findings
            </div>
          )}
        </div>
      </div>

      <FindingDetailDrawer
        projectId={projectId}
        findingId={selectedCanonicalId}
        open={Boolean(selectedCanonicalId)}
        onOpenChange={(open) => !open && setSelectedCanonicalId(null)}
        fallbackRow={{
          title: activeCanonical?.title,
          type: activeCanonical?.type,
          severity: activeCanonical?.severity,
          state: activeCanonical?.state,
          priorityScore: activeCanonical?.priorityScore ?? null,
          priorityOverride: activeCanonical?.priorityOverride ?? null,
          firstSeenAt: activeCanonical?.firstSeenAt
            ? typeof activeCanonical.firstSeenAt === "string"
              ? activeCanonical.firstSeenAt
              : activeCanonical.firstSeenAt.toISOString()
            : undefined,
          lastSeenAt: activeCanonical?.lastSeenAt
            ? typeof activeCanonical.lastSeenAt === "string"
              ? activeCanonical.lastSeenAt
              : activeCanonical.lastSeenAt.toISOString()
            : undefined,
          personaName: activeCanonical?.personas[0]?.name ?? null,
          preview:
            activeCanonical?.evidence?.consequence ??
            activeCanonical?.evidence?.uiFact ??
            null,
        }}
        routeEvidence={activeCanonical?.evidence ?? null}
      />
    </div>
  );
}
